import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import { SKILL_BY_ID, labelFor } from '../../../data/skillOntology';
import { getResourcesFor } from '../../../data/learningResources';

/**
 * LearningRecommendations
 * --------------------------------------------------------------------------
 * For each Growth Area (skill the user is missing), surface 2-3 hand-curated
 * learning resources. Resources are real platforms / programmes / books;
 * they're not linked because URLs change and we won't fabricate them.
 *
 * The user finds the current edition on the provider's site themselves.
 *
 * No call to an LLM here, the resources come from a static, reviewed
 * dictionary in /src/data/learningResources.js so investors don't have to
 * worry about hallucinated course names or live API costs.
 */

const TYPE_ICONS = {
  course:    'Play',
  cert:      'Award',
  book:      'BookOpen',
  programme: 'GraduationCap',
};

const TYPE_LABELS = {
  course:    'employeePortal.profile.learnTypeCourse',
  cert:      'employeePortal.profile.learnTypeCert',
  book:      'employeePortal.profile.learnTypeBook',
  programme: 'employeePortal.profile.learnTypeProgramme',
};

const ResourceItem = ({ resource }) => {
  const { t } = useTranslation();
  const iconName = TYPE_ICONS[resource.type] || 'BookOpen';
  const typeLabel = t(TYPE_LABELS[resource.type] || TYPE_LABELS.course);

  return (
    <div className="flex items-start gap-3 py-2.5 px-3 border border-border rounded-md bg-card hover:bg-muted/30 transition-smooth">
      <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
        <Icon name={iconName} size={14} className="text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground leading-snug break-words">{resource.title}</p>
        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground flex-wrap">
          <span>{resource.provider}</span>
          <span>·</span>
          <span className="uppercase tracking-wide font-medium">{typeLabel}</span>
          {resource.hours > 0 && (
            <>
              <span>·</span>
              <span>{t('employeePortal.profile.learnHours', { hours: resource.hours })}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const LearningRecommendations = ({ missingSkillIds, lang }) => {
  const { t } = useTranslation();

  // Sort skills with resources first, those without come last (and we only
  // render those with resources in the main list).
  const skillsWithResources = missingSkillIds
    .map((id) => ({ id, skill: SKILL_BY_ID[id], resources: getResourcesFor(id) }))
    .filter((x) => x.skill && x.resources.length > 0);

  if (skillsWithResources.length === 0) return null;

  return (
    <div className="mt-6 pt-5 border-t border-border">
      <div className="flex items-center gap-2 mb-1">
        <Icon name="Lightbulb" size={18} className="text-primary" />
        <h4 className="font-semibold text-foreground">{t('employeePortal.profile.learnTitle')}</h4>
      </div>
      <p className="text-sm text-muted-foreground mb-4">{t('employeePortal.profile.learnSubtitle')}</p>

      <div className="space-y-5">
        {skillsWithResources.map(({ id, skill, resources }) => (
          <div key={id}>
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border bg-warning/10 text-warning border-warning/30">
                <Icon name="Target" size={11} />
                {labelFor(skill, lang)}
              </span>
              <span className="text-xs text-muted-foreground">
                {t('employeePortal.profile.learnFor', { count: resources.length })}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-1">
              {resources.map((r, i) => <ResourceItem key={i} resource={r} />)}
            </div>
          </div>
        ))}
      </div>

      {/* Disclaimer */}
      <div className="mt-5 flex items-start gap-2 text-xs text-muted-foreground">
        <Icon name="Info" size={13} className="shrink-0 mt-0.5" />
        <span>{t('employeePortal.profile.learnDisclaimer')}</span>
      </div>
    </div>
  );
};

export default LearningRecommendations;
